import { useState, useEffect, useCallback } from 'react';
import { api } from '@/utils/api';

export interface PrizeBand {
  amount: number;
  weight: number;
}

export interface PhotoRequirement {
  key: string;
  label: string;
  required: boolean;
}

export interface TierLevel {
  name: string;
  threshold: number;
  perUnit: number;
  multiplier: number;
}

export interface UpsellEntry {
  key: string;
  label: string;
  bonus: number;
}

export interface PeriodicRewardConfig {
  targetCount: number;
  rewardType: 'GIFT_HAMPER' | 'TRIP' | 'CASH' | 'VOUCHER';
  rewardName: string;
  rewardValue: number;
}

export interface FieldProgramConfig {
  appName: string;
  partnerType: string;
  partnerTypeLabel: string;
  brandName: string;
  logoUrl?: string;
  primaryColor: string;
  tagline?: string;
  defaultLanguages: string[];
  defaultLanguage: string;
  kycRequired: boolean;
  kycVendor: string;
  demoMode: boolean;
  demoModeNotice?: string;
  prizeStructure: PrizeBand[];
  photoRequirements: PhotoRequirement[];
  tierStructure: TierLevel[];
  upsellCatalog: UpsellEntry[];
  quarterlyRewardConfig?: PeriodicRewardConfig | null;
  halfYearlyRewardConfig?: PeriodicRewardConfig | null;
  enabled: boolean;
}

interface UseFieldProgramConfigResult {
  config: FieldProgramConfig | null;
  enabled: boolean | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

// Ishan F-06: seeded Sun Stellar DB config stores `prizeStructure`,
// `photoRequirements`, `tierStructure`, `upsellCatalog` as OBJECTS, not arrays
// (e.g. `prizeStructure: { perInstallation, quarterlyTopPlumber, ... }`).
// Frontend types expect arrays and calls `.reduce`, `.map`, `.filter` on them
// → hard TypeError crash on load. This normalizer coerces every collection
// field to an array (or [] if shape is unrecognised) BEFORE state is set, so
// downstream code never sees the legacy object shape. Long term the seed
// should be migrated; this guard is defensive.
function toArray<T = any>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[];
  if (v == null) return [];
  if (typeof v === 'object') {
    // Legacy object shape — best-effort adapter for the known keys we've
    // seen in prod seeds. Anything else falls back to an empty array so the
    // page still renders and the user can re-save the new shape.
    const obj = v as Record<string, any>;
    if ('perInstallation' in obj || 'quarterlyTopPlumber' in obj) {
      const bands: any[] = [];
      if (obj.perInstallation != null) bands.push({ amount: Number(obj.perInstallation) || 0, weight: 1 });
      return bands as T[];
    }
    if ('requiredTypes' in obj && Array.isArray(obj.requiredTypes)) {
      return obj.requiredTypes.map((t: any) => ({ key: t, label: t, required: true })) as T[];
    }
    if ('products' in obj && Array.isArray(obj.products)) {
      return obj.products as T[];
    }
    if ('gold' in obj || 'silver' in obj || 'bronze' in obj) {
      const tiers: any[] = [];
      for (const name of ['bronze', 'silver', 'gold'] as const) {
        if (obj[name]) {
          tiers.push({
            name,
            threshold: Number(obj[name].min) || 0,
            perUnit: Number(obj[name].perUnit) || 0,
            multiplier: Number(obj[name].multiplier) || 1,
          });
        }
      }
      return tiers as T[];
    }
    return [];
  }
  return [];
}

function normalizeConfig(data: any): FieldProgramConfig | null {
  if (!data) return null;
  return {
    ...data,
    prizeStructure: toArray<PrizeBand>(data.prizeStructure),
    photoRequirements: toArray<PhotoRequirement>(data.photoRequirements),
    tierStructure: toArray<TierLevel>(data.tierStructure),
    upsellCatalog: toArray<UpsellEntry>(data.upsellCatalog),
  };
}

export function useFieldProgramConfig(): UseFieldProgramConfigResult {
  const [config, setConfig] = useState<FieldProgramConfig | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<{ data: FieldProgramConfig }>(
        '/field-partners/field-program-config',
      );
      const data = (res.data as any).data ?? res.data;
      setConfig(normalizeConfig(data));
      setEnabled(Boolean(data?.enabled));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setEnabled(false);
        setConfig(null);
      } else {
        setEnabled(false);
        setConfig(null);
        setError(err?.response?.data?.error || 'Failed to load field program config');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { config, enabled, loading, error, reload: load };
}

export default useFieldProgramConfig;
